import { useRef, useState, useEffect, useMemo } from "react";
import axios from "axios";

import { useDocumentModal } from "../../common/CustomModal/CustomModalContext";
import { DetailedViewCard } from "../DetailedViewCards";
import { mediaRoute } from "../../../Utils/helper";

import HeaderSection from "./headerSection";

const CARD_MIN_WIDTH = 255;
const GAP = 5;

const NewDocumentTab = ({ data, refetchData }) => {
  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(1);
  const [cardWidth, setCardWidth] = useState(CARD_MIN_WIDTH);
  const { showDocumentModal } = useDocumentModal();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateLayout = () => {
      const containerWidth = el.getBoundingClientRect().width;
      // console.log(containerWidth);
      // Calculate how many cards can fit at minimum width
      const perRow = Math.floor((containerWidth + GAP) / (CARD_MIN_WIDTH + GAP));
      const validPerRow = perRow || 1;

      // Calculate the actual width to distribute space evenly
      const totalGapWidth = (validPerRow - 1) * GAP;
      const availableWidth = containerWidth - totalGapWidth;
      const calculatedCardWidth = availableWidth / validPerRow;

      setCardsPerRow(validPerRow);
      setCardWidth(calculatedCardWidth);
    };

    const observer = new ResizeObserver(updateLayout);
    observer.observe(el);

    updateLayout();

    return () => observer.disconnect();
  }, []);
  const rows = useMemo(() => {
    const flatDocs = [];

    if (data === undefined || data === null) {
      return [];
    }
    data?.results?.forEach((section) => {
      const sectionName = section.page?.name || "Unsorted";
      const isPanels = section?.page?.panels || false;
      const sectionIcon =
        section.page?.page_icon ||
        "https://simplefirm-bucket.s3.amazonaws.com/static/images/documents-icon-color_b6TvcB7.svg";

      const docIdToPanelMap = new Map();
      const doc = section;

      if (isPanels) {
        docIdToPanelMap.set(doc.id, {
          panelName: doc?.panel?.panel_name || "",
          panelId: doc?.panel?.panel_id || null,
          specialty: doc?.panel?.specialty || null,
        });
      }

      const panelInfo = docIdToPanelMap.get(doc.id);
      flatDocs.push({
        ...doc,
        sectionName,
        sectionIcon,
        isPanels: isPanels,
        panelName: panelInfo?.panelName || null,
        panelId: panelInfo?.panelId || null,
        specialty: panelInfo?.specialty || null,
      });
    });

    const result = [];
    let idx = 0;
    while (idx < flatDocs.length) {
      const rowDocs = flatDocs?.slice(idx, idx + cardsPerRow);
      const sectionGroups = {};

      rowDocs?.forEach((doc) => {
        const key = doc.sectionName;
        if (!sectionGroups[key]) {
          sectionGroups[key] = {
            icon: doc.sectionIcon,
            isPanels: doc.isPanels,
            panelName: doc.panelName,
            panelId: doc.panelId,
            specialty: doc.specialty,
            docs: [],
          };
        }
        sectionGroups[key].docs.push(doc);
      });

      result.push(sectionGroups);
      idx += cardsPerRow;
    }

    return result;
  }, [cardsPerRow, data]);

  const handleDocPreview = (doc) => {
    axios
      .get(`${origin}/api/attorney-staff/${doc.attached_by?.id}/profile-image/`)
      .then((response) => {
        doc.attached_by.profile_pic_29p = mediaRoute(response.data.profile_image);
      })
      .catch((error) => {
        console.error(`Error fetching profile image: ${error}`);
      });

    showDocumentModal("document", doc.upload, doc, refetchData);
  };

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {rows.map((rowGroup, rowIndex) => {
        // Calculate total cards in this row
        const totalCardsInRow = Object.values(rowGroup).reduce(
          (sum, page) => sum + page.docs?.length,
          0
        );

        return (
          <div key={rowIndex} style={{ marginBottom: 5 }}>
            <div style={{ display: "flex", gap: GAP }}>
              {Object.entries(rowGroup).map(([sectionName, page], groupIndex) => {
                // Calculate if this is the last section in the row
                const isLastSection = groupIndex === Object.entries(rowGroup)?.length - 1;

                // Calculate cumulative cards before this section
                let cardsBeforeThisSection = 0;
                Object.entries(rowGroup)
                  .slice(0, groupIndex)
                  .forEach(([_, p]) => {
                    cardsBeforeThisSection += p?.docs?.length;
                  });

                const cardsInThisSection = page?.docs?.length;
                const _cardsAfterThisSection =
                  totalCardsInRow - cardsBeforeThisSection - cardsInThisSection;

                // If this is the last section and there's empty space, calculate header width
                const shouldExpandHeader = isLastSection && totalCardsInRow < cardsPerRow;
                const headerWidth = shouldExpandHeader
                  ? cardWidth * cardsInThisSection +
                    GAP * (cardsInThisSection - 1) +
                    (cardWidth + GAP) * (cardsPerRow - totalCardsInRow) +
                    GAP
                  : cardWidth * cardsInThisSection + GAP * (cardsInThisSection - 1);

                return (
                  <div
                    key={groupIndex}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <HeaderSection
                      name={`${sectionName} Page Documents`}
                      icon={page.icon}
                      width={headerWidth}
                    />

                    <div style={{ display: "flex", gap: GAP }}>
                      {page?.docs.map((doc, docIndex) => {
                        let cardPositionInRow = 0;
                        Object.entries(rowGroup).forEach(([, pg], idx) => {
                          if (idx < groupIndex) {
                            cardPositionInRow += pg.docs.length;
                          }
                        });
                        cardPositionInRow += docIndex;
                        return (
                          <DetailedViewCard
                            key={doc.id}
                            doc={doc}
                            slot={doc?.document_slot}
                            handleDocPreview={handleDocPreview}
                            cardWidth={cardWidth}
                            rowIndex={rowIndex}
                            cardPositionInRow={cardPositionInRow}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NewDocumentTab;
